import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock, CreditCard, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Separator } from "../components/ui/separator";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";
import { trackEvent, toGaItem } from "../lib/analyticsEvents";

export default function CheckoutPage() {
	const { items, total } = useCart();
	const { user, profile, isAuthenticated } = useAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [pickupType, setPickupType] = useState("pickup");
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		notes: "",
	});

	// Pré-remplit avec les données utilisateur si connecté
	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			firstName: profile?.first_name || prev.firstName,
			lastName: profile?.last_name || prev.lastName,
			email: user?.email || prev.email,
			phone: profile?.phone || prev.phone,
		}));
	}, [profile, user]);

	useEffect(() => {
		if (!items.length) return;

		trackEvent("view_checkout_page", {
			currency: "CHF",
			value: Number(total || 0),
			items: items.map((item) => toGaItem(item, item.quantity || 1)),
		});
	}, [items, total]);

	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(price);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (items.length === 0) {
			toast.error("Votre panier est vide");
			return;
		}

		if (!formData.firstName || !formData.lastName || !formData.email) {
			toast.error("Veuillez remplir tous les champs obligatoires");
			return;
		}

		setIsLoading(true);

		try {
			// ✅ UNE SEULE récupération de session
			const { data: sessionData } = await supabase.auth.getSession();
			const currentUserId = sessionData?.session?.user?.id || null;
			const accessToken = sessionData?.session?.access_token || null;

			console.log("SESSION AVANT INVOKE:", {
				hasSession: !!sessionData?.session,
				userId: currentUserId,
				token: accessToken ? "présent" : "absent",
			});

			const payload = {
				origin_url: window.location.origin,
				customer: {
					user_id: currentUserId,
					first_name: formData.firstName,
					last_name: formData.lastName,
					email: formData.email,
					phone: formData.phone || null,
				},
				items: items.map((item) => ({
					product_id: item.product_id || item.id,
					variant_id: item.variant_id || null,
					name: item.name,
					variant_name: item.variant_name || null,
					price: item.price,
					quantity: item.quantity,
					notes: item.notes || null,
				})),
				pickup_type: pickupType,
				notes: formData.notes || null,
			};

			// ✅ Token explicitement dans les headers
			const { data, error } = await supabase.functions.invoke(
				"create-checkout-session",
				{
					body: payload,
					headers: accessToken
						? { Authorization: `Bearer ${accessToken}` }
						: {},
				},
			);

			if (error) {
				console.error("Edge Function error:", error);
				throw new Error(
					error?.message || "Erreur lors de la création de la session",
				);
			}

			if (!data?.url) {
				throw new Error("URL de paiement non reçue");
			}

			trackEvent("add_payment_info", {
				currency: "CHF",
				value: Number(total || 0),
				payment_type: "Stripe",
				pickup_type: pickupType,
				items: items.map((item) => toGaItem(item, item.quantity || 1)),
			});

			window.location.href = data.url;
		} catch (err) {
			console.error("Checkout error:", err);
			toast.error(
				err?.message || "Une erreur est survenue. Veuillez réessayer.",
			);
		} finally {
			setIsLoading(false);
		}
	};

	if (items.length === 0) {
		return (
			<main
				data-testid="checkout-page"
				className="min-h-screen pt-20 bg-warm-alabaster"
			>
				<div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 text-center">
					<h1 className="font-serif text-2xl text-espresso mb-4">
						Votre panier est vide
					</h1>
					<Button asChild className="btn-secondary">
						<Link to="/menu">Voir le Menu</Link>
					</Button>
				</div>
			</main>
		);
	}

	return (
		<main
			data-testid="checkout-page"
			className="min-h-screen pt-20 bg-warm-alabaster"
		>
			<section className="bg-warm-secondary py-12 lg:py-16">
				<div className="max-w-7xl mx-auto px-6 lg:px-12">
					<Link
						to="/panier"
						className="inline-flex items-center text-soft-charcoal-secondary hover:text-espresso transition-colors duration-300 mb-4"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Retour au panier
					</Link>
					<h1 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
						Paiement
					</h1>
				</div>
			</section>

			<section className="py-12 lg:py-16">
				<div className="max-w-7xl mx-auto px-6 lg:px-12">
					<form onSubmit={handleSubmit}>
						<div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
							<div className="lg:col-span-2 space-y-8">
								<div className="bg-white p-6 border border-border-subtle">
									<h2 className="font-serif text-xl font-semibold text-espresso mb-6">
										Vos Informations
									</h2>

									{!isAuthenticated && (
										<p className="text-sm text-soft-charcoal-secondary mb-6">
											<Link
												to="/connexion"
												className="text-latte-gold hover:underline"
											>
												Connectez-vous
											</Link>{" "}
											pour pré-remplir vos informations et retrouver
											l'historique de vos commandes.
										</p>
									)}

									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<div className="space-y-2">
											<Label htmlFor="firstName">Prénom *</Label>
											<Input
												id="firstName"
												name="firstName"
												value={formData.firstName}
												onChange={handleInputChange}
												required
												className="input-field"
												data-testid="checkout-firstname"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="lastName">Nom *</Label>
											<Input
												id="lastName"
												name="lastName"
												value={formData.lastName}
												onChange={handleInputChange}
												required
												className="input-field"
												data-testid="checkout-lastname"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="email">Email *</Label>
											<Input
												id="email"
												name="email"
												type="email"
												value={formData.email}
												onChange={handleInputChange}
												required
												className="input-field"
												data-testid="checkout-email"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="phone">Téléphone</Label>
											<Input
												id="phone"
												name="phone"
												type="tel"
												value={formData.phone}
												onChange={handleInputChange}
												className="input-field"
												data-testid="checkout-phone"
											/>
										</div>
									</div>
								</div>

								<div className="bg-white p-6 border border-border-subtle">
									<h2 className="font-serif text-xl font-semibold text-espresso mb-6">
										Mode de Retrait
									</h2>

									<RadioGroup
										value={pickupType}
										onValueChange={setPickupType}
										className="space-y-4"
									>
										<div className="flex items-start space-x-3 p-4 border border-border-subtle hover:border-latte-gold transition-colors duration-300">
											<RadioGroupItem
												value="pickup"
												id="pickup"
												className="mt-1"
											/>
											<Label htmlFor="pickup" className="flex-1 cursor-pointer">
												<div className="flex items-center space-x-2">
													<Store className="w-5 h-5 text-latte-gold" />
													<span className="font-medium">Retrait sur place</span>
												</div>
												<p className="text-sm text-soft-charcoal-secondary mt-1">
													Récupérez votre commande au comptoir
													<br />
													Pl. des Augustins 19, 1205 Genève
												</p>
											</Label>
										</div>
									</RadioGroup>
								</div>

								<div className="bg-white p-6 border border-border-subtle">
									<h2 className="font-serif text-xl font-semibold text-espresso mb-6">
										Instructions (optionnel)
									</h2>
									<Textarea
										name="notes"
										value={formData.notes}
										onChange={handleInputChange}
										placeholder="Instructions spéciales pour votre commande..."
										className="min-h-[100px] border-border-subtle focus:border-espresso rounded-none resize-none"
										data-testid="checkout-notes"
									/>
								</div>
							</div>

							<div className="lg:col-span-1">
								<div className="bg-white p-6 border border-border-subtle sticky top-28">
									<h2 className="font-serif text-xl font-semibold text-espresso mb-6">
										Votre Commande
									</h2>

									<ul className="space-y-4 mb-6">
										{items.map((item) => (
											<li
												key={item.cart_item_id}
												className="flex justify-between text-sm gap-2"
											>
												<div className="min-w-0 flex-1">
													<span className="text-soft-charcoal block">
														{item.name}
														{item.variant_name && (
															<span className="text-soft-charcoal-secondary">
																{" "}
																— {item.variant_name}
															</span>
														)}{" "}
														× {item.quantity}
													</span>
													{item.notes && (
														<span className="text-xs italic text-soft-charcoal-secondary">
															Note : {item.notes}
														</span>
													)}
												</div>
												<span className="text-soft-charcoal font-medium whitespace-nowrap">
													{formatPrice(item.price * item.quantity)}
												</span>
											</li>
										))}
									</ul>

									<Separator className="bg-border-subtle mb-4" />

									<div className="flex justify-between mb-6">
										<span className="font-serif font-semibold text-espresso">
											Total
										</span>
										<span className="font-serif font-semibold text-espresso text-xl">
											{formatPrice(total)}
										</span>
									</div>

									<Button
										type="submit"
										disabled={isLoading}
										className="w-full btn-primary"
										data-testid="pay-now-btn"
									>
										{isLoading ? (
											<span className="flex items-center">
												<span className="spinner mr-2" />
												Chargement...
											</span>
										) : (
											<>
												<Lock className="w-4 h-4 mr-2" />
												Payer {formatPrice(total)}
											</>
										)}
									</Button>

									<div className="flex items-center justify-center mt-4 text-xs text-soft-charcoal-secondary">
										<CreditCard className="w-4 h-4 mr-2" />
										Paiement sécurisé par Stripe
									</div>
								</div>
							</div>
						</div>
					</form>
				</div>
			</section>
		</main>
	);
}
